import { useEffect, useState, useCallback } from "react";
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useJwt } from "../../helpers/common";
import CreateRoadMapsModal from "./CreateRoadMapsModal";
import ImportProjectModal from "../../components/Modals/ImportProjectModal";
import { useRoadmapService, useOrganizationService } from '../../services';
import { useSelector, useDispatch } from "react-redux";
import { DeleteModal } from "../../components/Modals";
import { useSideNavContext } from "../../context/SideNavContext";
import { getTimeZoneDate } from "../../helpers/data-format";
import CustomButton from '../../components/common/CustomButton';
import { SearchInput, SingleSelect, MultiSelect, selectStyles, selectTheme, SelectOption } from '../../components/common/Search';
import { setRoadmapSearchTerm } from "../../redux/slices/roadmap";
import Select from 'react-select';
import moment from 'moment';
import { useResourceInfo } from '../../hooks/useResourceInfo';
import { useIntegrationStatus } from '../../hooks/useIntegrationStatus';

import { 
    HiPencil,
    HiPlusCircle, HiTrash,
    HiSearch,
    HiX,
    HiDownload,
    HiUser,
    HiLink
} from "react-icons/hi";


type SearchMode = 'name' | 'project';
type SortBy = 'newest' | 'oldest' | 'az' | 'za';

// Define missing types
interface Roadmap {
  id: string;
  name: string;
  image_url?: string;
  created_at: string;
  project_ids?: string[];
  
  integration_data?: {
    integration_type: string;
    integration_status: string;
    integration_id: string;
    integration_item_type: string;
    integration_url?: string;
  };
  created_by?: string;
}

interface Project {
  project_id: string;
  project_name: string;
}

const useDarkMode = () => {
    const [isDarkMode, setIsDarkMode] = useState(() => 
        document.documentElement.classList.contains('dark')
    );
    
    useEffect(() => {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'class') {
                    setIsDarkMode(document.documentElement.classList.contains('dark'));
                }
            });
        });
        
        observer.observe(document.documentElement, { attributes: true });
        
        return () => observer.disconnect();
    }, []);
    
    return isDarkMode;
};

const RoadMap = () => {
    const { azureImportLevelFive: showImportButtonFlag = false } = useFlags();
    const [showModal, setShowModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedRoadmap, setSelectedRoadmap] = useState<Roadmap | {}>({});
    const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
    const [searchMode, setSearchMode] = useState<SearchMode>('name');
    const [currentSearchType, setCurrentSearchType] = useState('roadmap');
    const [sortBy, setSortBy] = useState<SortBy>('newest');
    const [filterByStatus, setFilterByStatus] = useState<string>('');
    const [filterByProgress, setFilterByProgress] = useState<string>('');
    const [showImportButton, setShowImportButton] = useState(false);
    
    const dispatch = useDispatch();
    const JWTdata:any = useJwt()
    const org_id = JWTdata.org_id;
    const { fetchRoadmaps, deleteRoadmap, fetchMoreRoadmaps } = useRoadmapService();
    const { fetchAllProjects } = useOrganizationService();
    const { isOpenOnSmallScreens } = useSideNavContext();
    const { getResourceName } = useResourceInfo();
    
    const {
        roadmapsLoading,
        roadmaps=[],
        loadingMore,
        hasMore,
        searchTerm
    } = useSelector((state: any) => state.roadmap);
    
    const {
        data: projects = [],
        loading: projectsLoading
    } = useSelector((state: any) => state.organization);

    const isDarkMode = useDarkMode();
    
    const { isIntegrationActive } = useIntegrationStatus();
    
    useEffect(() => {
        if(org_id) {
            fetchRoadmaps(org_id, true, 1, '', 'roadmap');
        }
    }, [org_id]);
    
    // Fetch projects only once when component mounts and org_id is available
    useEffect(() => {
        if(org_id && !projects.length && !projectsLoading) {
            fetchAllProjects(org_id);
        }
    }, [org_id]);

    // Update import button visibility based on feature flag and integration status
    useEffect(() => {
        // Only show the Azure import button if Azure integration is active and the feature flag is enabled
        setShowImportButton(showImportButtonFlag && isIntegrationActive('azure'));
    }, [showImportButtonFlag, isIntegrationActive]);
    
    useEffect(() => {
        if(!showModal)
            setSelectedRoadmap({})
    }, [showModal]);

    const handleCreateModalToggle = () => {
        setShowModal(true)
    }
    const handleDeleteRoadmap = (data:any) => {
        setSelectedRoadmap(data)
        setShowDeleteConfirm(true)
    }
    const handleEditRoadmap = (data:any) => {
        setSelectedRoadmap(data)
        setShowModal(true)
    }
    const handleDelete = (status:boolean) => {
        setShowDeleteConfirm(status)
    }

    const submitDelete = async () => {
        const typedRoadmap = selectedRoadmap as Roadmap;
        if(typedRoadmap.id){
          await deleteRoadmap(typedRoadmap.id);
          setSelectedRoadmap({})
          setShowDeleteConfirm(false);
        }
    }

    // Added this function to handle load more button clicks
    const handleLoadMore = () => {
        if (org_id && hasMore && !loadingMore) {
            fetchMoreRoadmaps(org_id, currentSearchType);
        }
    };

    // Get project options for the dropdown
    const getProjectOptions = useCallback(() => {
        return projects.map((project: Project) => ({
            value: project.project_id,
            label: project.project_name
        }));
    }, [projects]);

    // Search mode options
    const searchModeOptions = [
        { value: 'name', label: 'Map Name' },
        { value: 'project', label: 'Project Used' }
    ];

    // Sort options
    const sortOptions = [
        { value: 'newest', label: 'Newest First' },
        { value: 'oldest', label: 'Oldest First' },
        { value: 'az', label: 'A-Z' },
        { value: 'za', label: 'Z-A' }
    ];

    // Filter options for source types - filtered based on active integrations
    const getStatusFilterOptions = useCallback(() => {
        const baseOptions = [{ value: '', label: 'All Sources' }];
        
        // Only include integration types that are active
        const integrationOptions = [
            { value: 'azure', label: 'Azure' },
            { value: 'jira', label: 'Jira' },
            { value: 'asana', label: 'Asana' },
            { value: 'monday', label: 'Monday' }
        ].filter(integration => isIntegrationActive(integration.value));
        
        // Add item types if we have any active integrations
        const itemTypeOptions = integrationOptions.length > 0 
            ? [
                { value: 'Epic', label: 'Epic' },
                { value: 'Task', label: 'Task' },
                { value: 'Bug', label: 'Bug' },
                { value: 'Story', label: 'Story' },
                { value: 'Subtask', label: 'Subtask' }
              ]
            : [];
            
        return [...baseOptions, ...integrationOptions, ...itemTypeOptions];
    }, [isIntegrationActive]);

    // Filter options for progress status
    const progressFilterOptions = [
        { value: '', label: 'All Statuses' },
        { value: 'Done', label: 'Done' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'New', label: 'New' },
        { value: 'To Do', label: 'To Do' },
        { value: 'Closed', label: 'Closed' },
        { value: 'Cancelled', label: 'Cancelled' }
    ];

    // Handle project selection change
    const handleProjectChange = (selectedOptions: SelectOption[]) => {
        setSelectedProjects(selectedOptions || []);
        
        // If projects are selected, combine their names for the search term and use project search type
        if (selectedOptions && selectedOptions.length > 0) {
            const projectNames = selectedOptions.map(option => option.label).join(',');
            dispatch(setRoadmapSearchTerm(projectNames));
            setCurrentSearchType('project');
            fetchRoadmaps(org_id, true, 1, projectNames, 'project');
        } else {
            dispatch(setRoadmapSearchTerm(''));
            setCurrentSearchType('roadmap');
            fetchRoadmaps(org_id, true);
        }
    };

    // Handle search input change
    const handleSearchChange = (value: string) => {
        if (searchMode === 'name') {
            dispatch(setRoadmapSearchTerm(value));
            setCurrentSearchType('roadmap');
            fetchRoadmaps(org_id, true, 1, value, 'roadmap');
        }
    };

    const handleImportSuccess = () => {
        // Refresh roadmaps after successful import
        if(org_id) {
            fetchRoadmaps(org_id, true, 1, searchTerm, currentSearchType);
        }
    };

    // Handle sort change
    const handleSortChange = (option: any) => {
        setSortBy(option.value as SortBy);
    };

    // Handle filter change for progress status
    const handleProgressFilterChange = (option: any) => {
        setFilterByProgress(option.value);
    };

    // Handler for source/status filter changes
    const handleStatusFilterChange = (option: any) => {
        setFilterByStatus(option.value);
    };

    // Function to sort roadmaps based on current sort option
    const getSortedRoadmaps = useCallback(() => {
        if (!roadmaps || roadmaps.length === 0) return [];
        
        // Filter by source/type and progress status
        const filtered = roadmaps.filter((map: Roadmap) => {
            // Apply source/type filter
            const passesSourceFilter = !filterByStatus || 
                (map.integration_data?.integration_type && 
                 map.integration_data.integration_type.toLowerCase() === filterByStatus.toLowerCase()) ||
                (map.integration_data?.integration_item_type && 
                 map.integration_data.integration_item_type === filterByStatus);
                
            // Apply progress status filter
            const passesProgressFilter = !filterByProgress || 
                (map.integration_data?.integration_status && map.integration_data.integration_status === filterByProgress);
                
            // Item must pass both filters to be included
            return passesSourceFilter && passesProgressFilter;
        });
        
        return [...filtered].sort((a: Roadmap, b: Roadmap) => {
            switch (sortBy) {
                case 'newest':
                    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
                case 'oldest':
                    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                case 'az':
                    return a.name.localeCompare(b.name);
                case 'za':
                    return b.name.localeCompare(a.name);
                default:
                    return 0;
            }
        });
    }, [roadmaps, sortBy, filterByStatus, filterByProgress]);

    let w = "100%";
    if(!isOpenOnSmallScreens) {
        w = "95%";
    } else {
        w = "90%";
    }
    
    // Get the status filter options based on active integrations
    const statusFilterOptions = getStatusFilterOptions();

    // RoadmapSkeletonCard for loading state
    const RoadmapSkeletonCard = () => (
        <div className="mb-5 relative w-[360px] max-w-full animate-pulse">
            <div className="mb-3.5 h-[320px] w-full border border-gray-600 rounded-lg bg-gray-300 dark:bg-gray-700"></div>
            <div className="h-5 bg-gray-300 dark:bg-gray-700 rounded mb-1.5 w-3/4"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-800 rounded w-1/2"></div>
        </div>
    );

    const shouldShowIntegrationBadge = (data: Roadmap) => {
        const integrationType = data.integration_data?.integration_type || '';
        if (!integrationType) return false;
        
        return isIntegrationActive(integrationType);
    };

    return (
        <div className="bg-white dark:bg-[#111928] w-full relative h-screen flex flex-col">
            {(roadmapsLoading && !loadingMore && 
                        <div className="flex items-center fixed flex-col justify-center w-full top-0 bottom-0 ">
                            <img alt="cadence Loader" className="w-40 mb-1" src="https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/loader.gif" />
                            <div className="text-center dark:text-white opacity-50 text-base font-normal">Cadence is working<br />on your request</div>
                            <div className="absolute opacity-50 bg-slate-900 top-0 right-0 left-0 bottom-0"></div>
                        </div>
            )}
            
            {/* Modern Header with Background */}
            <div className="relative w-full">
                {/* Header Background with Gradient */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:gray h-40 z-0"></div>
                
                {/* Header Content */}
                <div className="relative z-10 px-4 sm:px-6 pt-8 pb-20 w-full">
                    <div className="w-full flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0">
                        <div className="max-w-3xl">
                            <h1 className="text-3xl font-bold text-slate-800 dark:text-white mb-2">
                                Strategic Maps
                            </h1>
                            <p className="text-slate-500 dark:text-slate-300 mb-0 pr-4">
                                Create, track, and visualize your strategic roadmaps for effective project planning and execution.
                            </p>
                        </div>
                        
                        {/* Action Buttons moved to top right */}
                        <div className="flex gap-3 flex-shrink-0 self-end sm:self-auto">
                            {showImportButton && (
                                <CustomButton
                                    onClick={() => setShowImportModal(true)}
                                    leftIcon={<HiDownload className="text-xl" />}
                                    className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-4 py-2"
                                >
                                    Import from Azure
                                </CustomButton>
                            )}
                            <CustomButton
                                onClick={handleCreateModalToggle}
                                leftIcon={<HiPlusCircle className="text-xl" />}
                                className="whitespace-nowrap bg-blue-600 hover:bg-blue-700 text-white px-4 py-2"
                            >
                                Create Strategic Map
                            </CustomButton>
                        </div>
                    </div>
                </div>
                
                {/* Advanced Filter Card - Positioned to overlap the header and content */}
                <div className="relative z-20 px-2 sm:px-4 -mt-16 w-full">
                    <div className="bg-white dark:bg-slate-800 rounded-xl shadow-md p-5 border border-slate-200 dark:border-slate-700 w-full">
                        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                            {/* Left Side - Search Controls */}
                            <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-12 gap-3">
                                <div className="sm:col-span-3">
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Search By</label>
                                    <SingleSelect
                                        name="searchMode"
                                        options={searchModeOptions}
                                        placeholder="Search by..."
                                        onChange={(option) => {
                                            setSearchMode(option.value as SearchMode);
                                            if (option.value === 'name') {
                                                dispatch(setRoadmapSearchTerm(''));
                                                setCurrentSearchType('roadmap');
                                                fetchRoadmaps(org_id, true, 1, '', 'roadmap');
                                            }
                                            setSelectedProjects([]);
                                        }}
                                        defaultValue={searchModeOptions.find(option => option.value === searchMode)}
                                        width="w-full"
                                    />
                                </div>
                                
                                <div className="sm:col-span-9">
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                                        {searchMode === 'name' ? 'Map Name' : 'Projects Used'}
                                    </label>
                                    <div className="w-full">
                                        {searchMode === 'name' ? (
                                            <div className="flex-grow w-full">
                                                <SearchInput
                                                    value={searchTerm}
                                                    onChange={(e) => handleSearchChange(e.target.value)}
                                                    placeholder="Search by map name..."
                                                    width="w-full"
                                                />
                                            </div>
                                        ) : (
                                            <MultiSelect
                                                name="projects"
                                                options={getProjectOptions()}
                                                onChange={handleProjectChange}
                                                placeholder="Select to search by projects used..."
                                                width="w-full"
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Right Side - Sort and Filter Controls */}
                            <div className="md:col-span-5 grid grid-cols-1 sm:grid-cols-12 gap-3">
                                <div className="sm:col-span-4">
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Sort Maps</label>
                                    <SingleSelect
                                        name="sortBy"
                                        options={sortOptions}
                                        placeholder="Sort by..."
                                        onChange={handleSortChange}
                                        defaultValue={sortOptions.find(option => option.value === sortBy)}
                                        width="w-full"
                                    />
                                </div>
                                
                                <div className="sm:col-span-4">
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Filter Source</label>
                                    <SingleSelect
                                        name="filterStatus"
                                        options={statusFilterOptions}
                                        placeholder="Filter by source type..."
                                        onChange={handleStatusFilterChange}
                                        defaultValue={statusFilterOptions.find(option => option.value === filterByStatus)}
                                        width="w-full"
                                    />
                                </div>
                                
                                <div className="sm:col-span-4">
                                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Filter Progress</label>
                                    <SingleSelect
                                        name="filterProgress"
                                        options={progressFilterOptions}
                                        placeholder="Filter by status..."
                                        onChange={handleProgressFilterChange}
                                        defaultValue={progressFilterOptions.find(option => option.value === filterByProgress)}
                                        width="w-full"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="flex-1 overflow-hidden mt-6">
                <div className="h-full overflow-y-auto px-2 sm:px-4 md:px-6">
                    {(!roadmapsLoading || loadingMore) && roadmaps.length ? (
                        <section className="bg-white dark:bg-[#111928] py-3 sm:py-5 flex flex-wrap gap-4 sm:gap-6 w-full justify-start">
                            {getSortedRoadmaps().map((data: Roadmap)=>{
                                // Integration type badge - will use integration_type (azure, jira, etc)
                                const integrationType = data.integration_data?.integration_type || '';
                                // Map for integration source (azure, jira, etc)
                                const integrationTypeColors: Record<string, string> = {
                                    'azure': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
                                    'jira': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
                                    'asana': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
                                    'monday': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                };
                                const integrationTypeColor = integrationTypeColors[integrationType.toLowerCase()] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
                                
                                // Item type badge - will use integration_item_type (Epic, Task, etc)
                                const itemType = data.integration_data?.integration_item_type || '';
                                const itemTypeColors: Record<string, string> = {
                                    'Task': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
                                    'Bug': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
                                    'Story': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
                                    'Epic': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
                                    'Subtask': 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300'
                                };
                                const itemTypeColor = itemTypeColors[itemType] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
                                
                                // Status badge - will use integration_status (Done, In Progress, etc)
                                const progressStatus = data.integration_data?.integration_status || '';
                                const progressStatusColors: Record<string, string> = {
                                    'Done': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
                                    'In Progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
                                    'New': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
                                    'To Do': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
                                    'Closed': 'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-300',
                                    'Cancelled': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                };
                                const progressStatusColor = progressStatusColors[progressStatus] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
                                
                                // Check if this specific roadmap's integration should be shown
                                const showIntegration = shouldShowIntegrationBadge(data);
                                
                                return (
                                    <div className="mb-5 relative group w-[360px] max-w-full" key={data.id}>
                                        <a href={`/strategicmaps/${data.id}`}>
                                        <div className="mb-3.5 h-[320px] w-full border border-gray-600 rounded-lg relative">
                                            
                                                
                                                
                                                {/* Badges at bottom right corner */}
                                                <div className="absolute right-2 bottom-2 z-10 flex flex-wrap gap-1.5 max-w-[calc(100%-16px)]">
                                                    {showIntegration && (
                                                        <>
                                                            <span className={`text-xs font-medium px-2.5 py-0.5 rounded ${integrationTypeColor}`}>
                                                                {integrationType.toUpperCase()}
                                                            </span>

                                                            {data.integration_data?.integration_id && (
                                                                <a 
                                                                    href={data.integration_data.integration_url || `https://${data.integration_data.integration_type}.com/item/${data.integration_data.integration_id}`}
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded text-xs text-blue-600 dark:text-blue-300 hover:underline hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200 flex items-center"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    <HiLink className="mr-1" /> 
                                                                    {data.integration_data.integration_id}
                                                                </a>
                                                            )}
                                                            
                                                            {itemType && (
                                                                <span className={`text-xs font-medium px-2.5 py-0.5 rounded ${itemTypeColor}`}>
                                                                    {itemType}
                                                                </span>
                                                            )}
                                                            
                                                            {progressStatus && (
                                                                <span className={`text-xs font-medium px-2.5 py-0.5 rounded ${progressStatusColor}`}>
                                                                    {progressStatus}
                                                                </span>
                                                            )}
                                                            
                                                            
                                                        </>
                                                    )}
                                                </div>
                                                
                                                
                                                
                                                <img
                                                    className="w-full rounded-lg h-full"
                                                    src={data.image_url 
                                                        ? data.image_url 
                                                        : isDarkMode 
                                                            ? "https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/Roadmap_dark.png" 
                                                            : "https://cadence-fe-bucket.s3.us-east-2.amazonaws.com/images/Roadmap_light.png"
                                                    }
                                                    alt={data.name}
                                                    loading={"lazy"}
                                                />
                                            </div>
                                        
                                            <div title={data.name} className="text-xl font-normal font-['Inter'] text-gray-900 hover:text-blue-700 dark:text-white mb-1.5 truncate first-letter:uppercase">
                                                {data.name}
                                            </div>
                                        </a>
                                        <p className="opacity-30 text-gray-900 dark:text-white text-xs font-normal font-['Inter']">
                                            {data.created_by 
                                                ? `CREATED BY: ${getResourceName(data.created_by)} on ${String(getTimeZoneDate(data.created_at,'lll'))}` 
                                                : `CREATED ON: ${String(getTimeZoneDate(data.created_at,'lll'))}`}
                                        </p>
                                        <HiTrash 
                                            className="group-hover:block hidden text-xm mr-1 absolute right-2 top-2 cursor-pointer text-red-700" 
                                            onClick={()=>handleDeleteRoadmap(data)}
                                        />
                                        <HiPencil 
                                            className="group-hover:block hidden text-xm mr-1 absolute right-8 top-2 cursor-pointer text-green-700" 
                                            onClick={()=>handleEditRoadmap(data)}
                                        />
                                    </div>  
                                )
                            })}
                            
                            {/* Show skeleton cards when loading more */}
                            {loadingMore && (
                                [...Array(5)].map((_, index) => (
                                    <RoadmapSkeletonCard key={`loading-more-${index}`} />
                                ))
                            )}
                        </section>
                    ) : (
                        <div 
                            title="Create a strategy map, A strategy map could be a roadmap with project phases or a go to market strategy plan." 
                            className="text-lg font-normal font-['Inter'] text-gray-900 dark:text-white mb-1.5 pt-10 pl-4 text-left"
                        >
                            {!roadmapsLoading ? 
                                (searchTerm || selectedProjects.length > 0 ? 'No strategic maps match your search criteria.' : 'Create a strategy map, A strategy map could be a roadmap with project phases or a go to market strategy plan.') 
                                : "Loading..."}
                        </div>
                    )}
                    
                    {/* Loading more indicator */}
                    {loadingMore && (
                        <div className="text-center py-1 text-xs text-blue-600 dark:text-blue-400 opacity-80">
                            Loading more strategic maps...
                        </div>
                    )}

                    {/* Load More button */}
                    {hasMore && !roadmapsLoading && !loadingMore && roadmaps.length > 0 && (
                        <div className="flex justify-center mt-2 mb-6">
                            <button 
                                className={`flex items-center justify-center gap-1 px-4 py-1.5 text-sm font-medium rounded-md transition-colors duration-200 border ${
                                    loadingMore 
                                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600' 
                                    : 'bg-white hover:bg-gray-50 text-blue-600 border-blue-600 hover:border-blue-700 dark:bg-slate-700 dark:text-blue-400 dark:border-blue-500 dark:hover:bg-slate-600'
                                }`}
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? (
                                    <>
                                        <svg className="animate-spin h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Loading...
                                    </>
                                ) : (
                                    <>
                                        Load More
                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-1">
                                            <path d="M7 10l5 5 5-5"/>
                                        </svg>
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                    
                   
                   
                </div>
            </div>
            {showModal && <CreateRoadMapsModal 
                isVisible={showModal}
                setShowModal={setShowModal}
                roadmap={selectedRoadmap}
                setSelectedRoadmap={setSelectedRoadmap}
            />}
            <ImportProjectModal 
                showModal={showImportModal}
                setShowModal={setShowImportModal}
                onImportSuccess={handleImportSuccess}
            />
            <DeleteModal showModal={showDeleteConfirm} setShowModal={handleDelete} handleConfirm={submitDelete} />
        </div>
    )
}

export default RoadMap;